import { formatDistance } from "date-fns"
import {
	IconGearOutline18 as Gear,
	IconArrowRotateClockwiseOutline18 as Sync,
} from "nucleo-ui-outline-18"

import { Button } from "@productlane/ui"

import type { RouterOutputs } from "@/lib/api"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { SettingItem } from "@/screens/settings/settings-switches/setting-item"
import { useDocsTabDialogs } from "@/stores/docs/docs-tab-store"
import { syncToast } from "./api-reference-page"

type SpecState = NonNullable<RouterOutputs["apiReference"]["get"]>["state"]

/** One line: where the spec comes from, its endpoint count, and when it last synced. */
export function apiReferenceSummary(state: SpecState, now: Date) {
	if (!state) return null
	const source =
		state.source.type === "url"
			? state.source.url.replace(/^https?:\/\//, "")
			: state.source.fileName
	const parts = [source]
	if (state.live) {
		const count = state.live.operations
		parts.push(`${count} endpoint${count === 1 ? "" : "s"}`)
	}
	const syncedAt = state.lastCheckedAt ?? state.live?.at
	if (syncedAt) {
		parts.push(
			`synced ${formatDistance(new Date(syncedAt), now, { addSuffix: true })}`,
		)
	}
	return parts.join(" · ")
}

export function ApiReferenceSettingsRow({
	tabId,
	name,
}: {
	tabId: string
	name: string
}) {
	const { setApiSettingsTabId } = useDocsTabDialogs()
	const query = api.apiReference.get.useQuery({ tabId })
	const utils = api.useUtils()
	const syncNow = api.apiReference.syncNow.useMutation({
		onSuccess: (result) => {
			syncToast(result)
			void utils.apiReference.get.invalidate({ tabId })
		},
		onError: (error) =>
			toast.error({ title: "Sync failed", description: error.message }),
	})
	const state = query.data?.state ?? null
	const summary = apiReferenceSummary(state, new Date())

	return (
		<SettingItem
			title={name}
			description={
				summary && (
					<span className="block max-w-[22rem] truncate" title={summary}>
						{summary}
					</span>
				)
			}
			action={
				<RoleCheck not={["VIEWER"]}>
					<div className="flex flex-none items-center gap-2">
						{state?.source.type === "url" && (
							<Button
								variant="ghost"
								Icon={Sync}
								isLoading={syncNow.isLoading}
								onClick={() => syncNow.mutate({ tabId })}
							>
								Sync now
							</Button>
						)}
						<Button Icon={Gear} onClick={() => setApiSettingsTabId(tabId)}>
							Settings
						</Button>
					</div>
				</RoleCheck>
			}
		/>
	)
}
